import { Button } from '@material-ui/core';
import React from 'react';
import { Control, useForm, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Translations } from '../../configuration';
import _ from 'lodash';
import { useHistory } from 'react-router';
import NavigationConfig from 'navigation/config';
import * as VatReturnSelectors from 'redux/reducers/vatReturn_list_reducer/selectors';
import { StateStatus } from 'redux/utils/common';
import { readableVatReturnState, readableVatReturnStatus } from 'config';

interface FormFields {
  vat_num: string | null;
  period: string | null;
  year: string | null;
  declaration_status: string | null;
  vat_return_state: string | null;
}

interface IProps {
  vat_num: string | null;
  period: string | null;
  year: string | null;
  declaration_status: string | null;
  vat_return_state: string | null;
}

const FilterForm = (props: IProps) => {
  const history = useHistory();
  const defaultValues = {
    vat_num: props.vat_num ? props.vat_num : null,
    period: props.period ? props.period : null,
    year: props.year ? props.year : null,
    declaration_status: props.declaration_status
      ? props.declaration_status
      : null,
    vat_return_state: props.vat_return_state ? props.vat_return_state : null,
  };
  const { control, errors, handleSubmit } = useForm<FormFields>({
    defaultValues: defaultValues,
  });

  const onSubmit = (data: any) => {
    let query = [];
    if (data.vat_num)
      query.push({
        key: 'vat_num',
        value: data.vat_num,
      });
    if (data.period)
      query.push({
        key: 'period',
        value: data.period,
      });
    if (data.year)
      query.push({
        key: 'year',
        value: new Date(data.year).getFullYear(),
      });
    if (data.declaration_status)
      query.push({
        key: 'declaration_status',
        value: data.declaration_status,
      });
    if (data.vat_return_state)
      query.push({
        key: 'vat_return_state',
        value: data.vat_return_state,
      });
    console.log(data);
    let queryString = '';
    console.log(query);
    query.map((query, index) => {
      if (index == 0) queryString += `?${query.key}=${query.value}`;
      else queryString += `&${query.key}=${query.value}`;
    });
    history.push(NavigationConfig.vatReturnsPage().path + queryString);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: renderVatNumberField(errors, control),
            size: 12,
            id: 'vatNumber',
          },
          {
            element: renderPeriodField(errors, control),
            size: 6,
            id: 'period',
          },
          {
            element: renderYearField(errors, control),
            size: 6,
            id: 'year',
          },
          {
            element: renderDeclarationStatusField(errors, control),
            size: 6,
            id: 'declarationStatus',
          },
          {
            element: renderVatReturnStatusField(errors, control),
            size: 6,
            id: 'vatReturnStatus',
          },
          {
            element: <span></span>,
            size: 10,
            id: 'space',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%', marginBottom: '1rem' }}
              >
                <TranslatedText textMap={Translations.search} />
              </Button>
            ),
            size: 2,
            id: 'action',
          },
        ]}
      />
    </form>
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="vat_num"
      label={
        <TranslatedText textMap={Translations.vat_number_search_control} />
      }
      error={'vat_num' in errors}
      name="vat_num"
      control={control}
      rules={{}}
      helperText={errors.vat_num && errors.vat_num.message}
    />
  );
};

const renderPeriodField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  const quarters = VatReturnSelectors.useGetQuarters();
  const monthQuarters = VatReturnSelectors.useGetMonthQuarterList();
  var select_options: any[] = [];
  select_options.push({
    id: null,
    label: Translations.period_search_control.English,
  });
  const periodOptions = [];
  if (quarters && quarters.status === StateStatus.Success && quarters.data) {
    periodOptions.push(...quarters.data);
  }
  if (
    monthQuarters &&
    monthQuarters.status === StateStatus.Success &&
    monthQuarters.data
  ) {
    periodOptions.push(...monthQuarters.data);
  }
  for (let quarter of periodOptions)
    select_options.push({
      id: quarter.quarter_id,
      label: quarter.short_text,
    });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.period_search_control} />}
      id="period"
      name="period"
      error={'period' in errors}
      control={control}
      rules={{}}
      options={select_options}
      helperText={errors.period && errors.period.message}
    />
  );
};

const renderYearField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      autoOk
      margin="normal"
      label={<TranslatedText textMap={Translations.year_search_control} />}
      openTo="year"
      views={['year']}
      error={'year' in errors}
      format={'yyyy'}
      control={control}
      rules={{}}
      name="year"
      helperText={errors.year && errors.year.message}
    />
  );
};

const renderDeclarationStatusField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  var select_options: any[] = [];
  select_options.push({
    id: null,
    label: Translations.declaration_status_search_control.English,
  });
  readableVatReturnStatus.map((value, index) => {
    select_options.push({
      id: `${index}`,
      label: value,
    });
  });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={
        <TranslatedText
          textMap={Translations.declaration_status_search_control}
        />
      }
      id="declaration_status"
      name="declaration_status"
      error={'declaration_status' in errors}
      control={control}
      rules={{}}
      options={select_options}
      helperText={
        errors.declaration_status && errors.declaration_status.message
      }
    />
  );
};

const renderVatReturnStatusField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  let select_options = [];
  select_options.push({
    id: null,
    label: Translations.vat_return_status_search_control.English,
  });

  for (let state in readableVatReturnState) {
    select_options.push({
      id: `${state}`,
      label: _.get(readableVatReturnState, state),
    });
  }
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={
        <TranslatedText
          textMap={Translations.vat_return_status_search_control}
        />
      }
      id="vat_return_state"
      name="vat_return_state"
      error={'vat_return_state' in errors}
      control={control}
      rules={{}}
      options={select_options as any}
      helperText={errors.vat_return_state && errors.vat_return_state.message}
    />
  );
};

export default FilterForm;
